import { useEffect, useMemo, useCallback } from 'react'
import { useTranslation, useForm, Controller, zodResolver, z } from '@ufc/core'
import {
  FormDrawer,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
  MapPin,
} from '@ufc/uikit'
import type {
  Address,
  CreateAddressRequest,
  City,
  LocationType,
  LocalizedText,
} from '../../domain/types'

// ============================================================================
// Schema
// ============================================================================

const addressFormSchema = z.object({
  nameEn: z.string().min(1, 'addressbook.validation.nameRequired'),
  nameAr: z.string().optional().default(''),
  addressLine1En: z.string().min(1, 'addressbook.validation.addressRequired'),
  addressLine1Ar: z.string().optional().default(''),
  addressLine2En: z.string().optional().default(''),
  addressLine2Ar: z.string().optional().default(''),
  cityId: z.string().min(1, 'addressbook.validation.cityRequired'),
  postalCode: z.string().optional().default(''),
  locationTypeId: z.string().optional().default(''),
})

type AddressFormData = z.infer<typeof addressFormSchema>

// ============================================================================
// Props
// ============================================================================

export interface AddressFormDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  address?: Address | null
  cities: City[]
  locationTypes?: LocationType[]
  allowedRegionIds?: string[]
  onSubmit: (data: CreateAddressRequest) => void
  isLoading?: boolean
  showLocationType?: boolean
  isSubmitting?: boolean
  error?: string | null
  onClearError?: () => void
  isRTL?: boolean
}

// ============================================================================
// Helpers
// ============================================================================

function buildLocalizedText(en: string, ar: string): LocalizedText {
  const t: LocalizedText = { en: en.trim() }
  const arTrim = ar.trim()
  if (arTrim) t.ar = arTrim
  return t
}

function getDefaultValues(address?: Address | null): AddressFormData {
  if (!address) {
    return {
      nameEn: '',
      nameAr: '',
      addressLine1En: '',
      addressLine1Ar: '',
      addressLine2En: '',
      addressLine2Ar: '',
      cityId: '',
      postalCode: '',
      locationTypeId: '',
    }
  }
  const name = address.nameI18n
  const line1 = address.addressLine1I18n
  const line2 = address.addressLine2I18n
  return {
    nameEn: name?.en ?? address.name ?? '',
    nameAr: name?.ar ?? '',
    addressLine1En: line1?.en ?? address.addressLine1 ?? address.address ?? '',
    addressLine1Ar: line1?.ar ?? '',
    addressLine2En: line2?.en ?? address.addressLine2 ?? '',
    addressLine2Ar: line2?.ar ?? '',
    cityId: address.cityId || '',
    postalCode: address.postalCode || '',
    locationTypeId: address.locationType?.id || address.locationType?.documentId || '',
  }
}

function toRequest(data: AddressFormData, city?: City): CreateAddressRequest {
  const addressLine2Lt =
    data.addressLine2En.trim() || data.addressLine2Ar.trim()
      ? buildLocalizedText(data.addressLine2En, data.addressLine2Ar)
      : undefined

  return {
    name: buildLocalizedText(data.nameEn, data.nameAr),
    addressLine1: buildLocalizedText(data.addressLine1En, data.addressLine1Ar),
    addressLine2: addressLine2Lt,
    cityId: data.cityId || undefined,
    countryId: city?.countryId || undefined,
    postalCode: data.postalCode?.trim() || undefined,
    locationTypeId: data.locationTypeId?.trim() || undefined,
  }
}

function filterCities(
  cities: City[],
  allowedRegionIds: string[] | undefined
): City[] {
  const allowed = (allowedRegionIds ?? []).filter(Boolean)
  if (allowed.length === 0) return cities
  return cities.filter(c => c.regionId && allowed.includes(c.regionId))
}

// ============================================================================
// Component
// ============================================================================

export function AddressFormDrawer({
  open,
  onOpenChange,
  address,
  cities,
  locationTypes = [],
  allowedRegionIds,
  onSubmit,
  isLoading = false,
  showLocationType = false,
  isSubmitting = false,
  error,
  onClearError,
  isRTL = false,
}: AddressFormDrawerProps) {
  const { t } = useTranslation()
  const isEditing = !!address

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: getDefaultValues(address),
  })

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(address))
    }
  }, [open, address?.documentId, reset, address])

  const filteredCities = useMemo(
    () => filterCities(cities, allowedRegionIds),
    [cities, allowedRegionIds]
  )

  const cityId = watch('cityId')
  const selectedCity = useMemo(
    () => cities.find(c => c.id === cityId),
    [cities, cityId]
  )

  const onFormSubmit = useCallback(
    (data: AddressFormData) => {
      onSubmit(toRequest(data, selectedCity))
    },
    [onSubmit, selectedCity]
  )

  const headerContent = (
    <div className={cn('flex items-center gap-3')}>
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <MapPin className="w-6 h-6 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-white">
          {isEditing ? t('addressbook.editAddress') : t('addressbook.addAddress')}
        </h2>
        <p className="text-sm text-white/80">
          {isEditing ? t('addressbook.editAddressDescription') : t('addressbook.addAddressDescription')}
        </p>
      </div>
    </div>
  )

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient="from-primary to-primary/80"
      headerContent={headerContent}
      maxWidth="sm:max-w-md"
      title={isEditing ? t('addressbook.editAddress') : t('addressbook.addAddress')}
      isLoading={isLoading && !address}
      isSubmitting={isSubmitting}
      error={error}
      onClearError={onClearError}
      onSave={handleSubmit(onFormSubmit)}
      saveText={t('common.save')}
      savingText={t('common.saving')}
      cancelText={t('common.cancel')}
    >
      <form id="address-form" onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
        <p className="text-xs font-medium text-muted-foreground">{t('addressbook.form.englishSection')}</p>

        <div className="space-y-1.5">
          <Label htmlFor="nameEn">{t('addressbook.form.locationNameEn')} *</Label>
          <Controller
            name="nameEn"
            control={control}
            render={({ field }) => (
              <Input
                id="nameEn"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('addressbook.locationNamePlaceholder')}
                className={cn('h-10', errors.nameEn && 'border-destructive')}
              />
            )}
          />
          {errors.nameEn && <p className="text-xs text-destructive">{t(errors.nameEn.message || '')}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="nameAr">{t('addressbook.form.locationNameAr')}</Label>
          <Controller
            name="nameAr"
            control={control}
            render={({ field }) => (
              <Input
                id="nameAr"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('addressbook.form.locationNameArPlaceholder')}
                className="h-10"
                dir="rtl"
              />
            )}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t('addressbook.city')} *</Label>
          <Controller
            name="cityId"
            control={control}
            render={({ field }) => (
              <Select value={field.value || ''} onValueChange={field.onChange}>
                <SelectTrigger className={cn('h-10', errors.cityId && 'border-destructive')}>
                  <SelectValue placeholder={t('addressbook.selectCity')} />
                </SelectTrigger>
                <SelectContent disablePortal>
                  {filteredCities.length === 0 ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      {isLoading ? t('common.loading') : t('addressbook.noCitiesAvailable')}
                    </div>
                  ) : (
                    filteredCities.map(city => (
                      <SelectItem key={city.id} value={city.id}>
                        {city.name}
                        {city.regionName && <span className="text-muted-foreground"> • {city.regionName}</span>}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          />
          {errors.cityId && <p className="text-xs text-destructive">{t(errors.cityId.message || '')}</p>}
        </div>

        {showLocationType && locationTypes.length > 0 && (
          <div className="space-y-1.5">
            <Label>{t('addressbook.locationType')}</Label>
            <Controller
              name="locationTypeId"
              control={control}
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder={t('addressbook.selectLocationType')} />
                  </SelectTrigger>
                  <SelectContent disablePortal>
                    {locationTypes.map(locationType => (
                      <SelectItem key={locationType.documentId} value={locationType.id || locationType.documentId}>
                        {locationType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        )}

        <p className="text-xs font-medium text-muted-foreground pt-2">{t('addressbook.form.streetSection')}</p>

        <div className="space-y-1.5">
          <Label htmlFor="addressLine1En">{t('addressbook.form.streetAddressEn')} *</Label>
          <Textarea
            id="addressLine1En"
            {...register('addressLine1En')}
            placeholder={t('addressbook.streetAddressPlaceholder')}
            className={cn('min-h-[70px]', errors.addressLine1En && 'border-destructive')}
          />
          {errors.addressLine1En && (
            <p className="text-xs text-destructive">{t(errors.addressLine1En.message || '')}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="addressLine1Ar">{t('addressbook.form.streetAddressAr')}</Label>
          <Textarea
            id="addressLine1Ar"
            {...register('addressLine1Ar')}
            placeholder={t('addressbook.form.streetAddressArPlaceholder')}
            className="min-h-[70px]"
            dir="rtl"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="addressLine2En">{t('addressbook.form.addressLine2En')}</Label>
            <Controller
              name="addressLine2En"
              control={control}
              render={({ field }) => (
                <Input
                  id="addressLine2En"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('addressbook.form.addressLine2Placeholder')}
                  className="h-10"
                />
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addressLine2Ar">{t('addressbook.form.addressLine2Ar')}</Label>
            <Controller
              name="addressLine2Ar"
              control={control}
              render={({ field }) => (
                <Input
                  id="addressLine2Ar"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('addressbook.form.addressLine2ArPlaceholder')}
                  className="h-10"
                  dir="rtl"
                />
              )}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="postalCode">{t('addressbook.postalCode')}</Label>
          <Controller
            name="postalCode"
            control={control}
            render={({ field }) => (
              <Input
                id="postalCode"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="12345"
                className="h-10"
                dir="ltr"
              />
            )}
          />
        </div>
      </form>
    </FormDrawer>
  )
}
